import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

const STORAGE_KEY = "cartal-lang";

const dict = {
  en: {
    "brand.tagline": "One Platform. Every Vehicle. Every Service.",
    "nav.marketplace": "Marketplace",
    "nav.rentals": "Rentals",
    "nav.auctions": "Auctions",
    "nav.financing": "Financing",
    "nav.insurance": "Insurance",
    "nav.inspection": "Inspection",
    "nav.prePurchaseInspection": "Pre-purchase Check-Up",
    "nav.periodicInspection": "Periodic Inspection",
    "nav.ownershipTransfer": "Ownership Transfer",
    "nav.vehicleHistory": "Mojaz Vehicle History",
    "nav.contact": "Contact Us",
    "nav.showrooms": "Showrooms by City",
    "nav.delivery": "Delivery",
    "nav.parts": "Parts",
    "nav.services": "Service",
    "nav.salvage": "Salvage",
    "nav.export": "Export",
    "nav.heavy": "Heavy Equipment",
    "nav.motorcycles": "Motorcycles",
    "nav.marine": "Yachts, Boats & Jet Skis",
    "nav.dispute": "Disputes",
    "nav.trust": "Trust & Safety",
    "nav.pricing": "Pricing",
    "nav.dashboard": "Dashboard",
    "nav.login": "Sign in",
    "nav.register": "Join CARTAL",
    "join.title": "Join CARTAL",
    "join.subtitle": "Choose the account type that fits you and continue in seconds.",
    "join.personal.title": "Personal account",
    "join.personal.tag": "For individuals",
    "join.personal.desc": "Buy, sell and manage your own vehicles on CARTAL.",
    "join.personal.cta": "Continue as personal",
    "join.business.title": "CARTAL Business",
    "join.business.tag": "For dealers, workshops & companies",
    "join.business.desc": "Get a verified business workspace with a dedicated dashboard, listings, employees and analytics.",
    "join.business.cta": "Continue as business",
    "join.already": "Already have an account?",
    "join.signin": "Sign in",
    "nav.logout": "Logout",
    "nav.modules": "All Modules",
    "nav.sell": "Sell a Vehicle",
    "home.eyebrow": "The Middle East's Automotive Super-App",
    "home.heroTitle1": "Every vehicle.",
    "home.heroTitle2": "Every service.",
    "home.heroTitle3": "One platform.",
    "home.heroSub":
      "Buy, sell, rent, finance, insure, inspect, transport, auction and service vehicles — all in one unified marketplace built for the Middle East.",
    "home.searchPlaceholder": "Search by make, model, VIN or ad # (CAR-…)",
    "home.searchBtn": "Search vehicles",
    "home.cta.browse": "Browse Marketplace",
    "home.cta.list": "List Your Vehicle",
    "home.featured": "Featured Vehicles",
    "home.featuredSub": "Hand-picked listings from verified dealers and agencies",
    "home.modules": "The CARTAL Ecosystem",
    "home.modulesSub":
      "Eighteen specialised modules covering every step of the vehicle lifecycle.",
    "home.howItWorks": "How CARTAL Works",
    "home.step1.t": "Discover",
    "home.step1.d": "Browse 100,000+ vehicles from agencies, dealers, and verified individuals.",
    "home.step2.t": "Verify",
    "home.step2.d": "Order an independent inspection and view the full vehicle history.",
    "home.step3.t": "Transact",
    "home.step3.d": "Pay through CARTAL Escrow — funds released only after delivery confirmation.",
    "home.trust": "Built on trust. Backed by technology.",
    "home.stats.vehicles": "Live vehicle listings",
    "home.stats.dealers": "Verified dealers & agencies",
    "home.stats.providers": "Service partners",
    "home.stats.auctions": "Active auctions",
    "home.partners": "Built to integrate with",
    "home.ai.title": "AI Vehicle Valuation",
    "home.ai.sub":
      "Get an instant market estimate powered by Claude. Just enter your vehicle details.",
    "common.viewAll": "View all",
    "common.from": "from",
    "common.perDay": "/ day",
    "common.year": "Year of manufacture",
    "common.km": "km",
    "common.condition": "Condition",
    "common.make": "Make",
    "common.model": "Model",
    "common.estimate": "Estimate value",
    "common.estimating": "Estimating…",
    "common.fairValue": "Fair Value",
    "common.range": "Range",
    "common.summary": "Summary",
    "common.book": "Book now",
    "common.applyNow": "Apply now",
    "common.compare": "Compare",
    "common.bidNow": "Bid now",
    "common.email": "Email",
    "common.password": "Password",
    "common.name": "Full name",
    "common.phone": "Phone",
    "common.role": "I am a…",
    "common.company": "Company name",
    "common.signIn": "Sign in",
    "common.createAccount": "Create account",
    "common.signingIn": "Signing in…",
    "common.creating": "Creating…",
    "common.loading": "Loading…",
    "common.empty": "Nothing here yet.",
    "common.legal":
      "CARTAL acts solely as a neutral technology platform connecting buyers, sellers and service providers. We are not a party to any transaction.",
    "footer.about": "About",
    "footer.terms": "Terms of Use",
    "footer.privacy": "Privacy Policy",
    "footer.disclaimer": "Legal Disclaimer",
    "footer.refund": "Refund Policy",
    "footer.fraud": "Fraud Prevention",
    "footer.contact": "Contact",
    "footer.copyright": "© 2026 CARTAL. All rights reserved.",
    "auth.signInTitle": "Welcome back",
    "auth.signInSub": "Sign in to access your CARTAL dashboard.",
    "auth.registerTitle": "Join CARTAL",
    "auth.registerSub": "Create a free account in seconds.",
    "auth.switchToRegister": "New to CARTAL?",
    "auth.switchToLogin": "Already have an account?",
    "auth.roles.individual_buyer": "Individual buyer",
    "auth.roles.individual_seller": "Individual seller",
    "auth.roles.business_member": "Business member",
    "auth.roles.dealer": "Vehicle dealer",
    "auth.roles.agency": "Vehicle agency",
    "auth.roles.showroom": "Car showroom",
    "auth.roles.rental": "Rental company",
    "auth.roles.financing": "Financing company",
    "auth.roles.insurance": "Insurance company",
    "auth.roles.inspection": "Inspection company",
    "auth.roles.logistics": "Logistics company",
    "auth.roles.workshop": "Workshop / service center",
    "auth.roles.parts": "Spare parts store",
    "auth.roles.fleet": "Fleet owner",
    "auth.roles.corporate": "Corporate customer",
    "auth.roles.exporter": "Exporter",
    "auth.roles.marine_operator": "Marine activity (yachts & boats)",
    "auth.roles.super_admin": "Super Admin",
    "auth.roles.admin": "Admin",
    "auth.roles.user": "User",
  },
  ar: {
    "brand.tagline": "منصة واحدة. لكل مركبة. لكل خدمة.",
    "nav.marketplace": "السوق",
    "nav.rentals": "التأجير",
    "nav.auctions": "المزادات",
    "nav.financing": "التمويل",
    "nav.insurance": "التأمين",
    "nav.inspection": "الفحص",
    "nav.prePurchaseInspection": "فحص قبل الشراء",
    "nav.periodicInspection": "الفحص الفني الدوري",
    "nav.ownershipTransfer": "نقل الملكية",
    "nav.vehicleHistory": "تقرير موجز",
    "nav.contact": "تواصل معنا",
    "nav.showrooms": "معارض السيارات",
    "nav.delivery": "التوصيل",
    "nav.parts": "قطع الغيار",
    "nav.services": "الصيانة",
    "nav.salvage": "السكراب",
    "nav.export": "التصدير",
    "nav.heavy": "المعدات الثقيلة",
    "nav.motorcycles": "الدراجات النارية",
    "nav.marine": "يخوت وزوارق ودبابات بحرية",
    "nav.dispute": "النزاعات",
    "nav.trust": "الأمان والثقة",
    "nav.pricing": "الباقات",
    "nav.dashboard": "لوحة التحكم",
    "nav.login": "تسجيل الدخول",
    "nav.register": "انضم إلى كارتال",
    "join.title": "انضم إلى كارتال",
    "join.subtitle": "اختر نوع الحساب المناسب لك وأكمل خطواتك في ثوانٍ.",
    "join.personal.title": "حساب شخصي",
    "join.personal.tag": "للأفراد",
    "join.personal.desc": "بِع واشترِ وأدِر مركباتك الشخصية على كارتال.",
    "join.personal.cta": "متابعة كحساب شخصي",
    "join.business.title": "CARTAL Business",
    "join.business.tag": "للتجّار والمعارض والشركات ومقدمي الخدمات",
    "join.business.desc": "احصل على مساحة عمل تجارية موثّقة بلوحة تحكم مخصصة، إعلانات، موظفين، وتحليلات.",
    "join.business.cta": "متابعة كحساب أعمال",
    "join.already": "لديك حساب مسبقاً؟",
    "join.signin": "تسجيل الدخول",
    "nav.logout": "تسجيل الخروج",
    "nav.modules": "جميع الوحدات",
    "nav.sell": "بيع مركبتك",
    "home.eyebrow": "التطبيق الشامل للسيارات في الشرق الأوسط",
    "home.heroTitle1": "كل مركبة.",
    "home.heroTitle2": "كل خدمة.",
    "home.heroTitle3": "منصة واحدة.",
    "home.heroSub":
      "اشترِ، بِع، استأجر، موّل، أمّن، افحص، انقل، نزايد، وصِيِّن مركبتك — كل ذلك في سوق موحد مصمم للشرق الأوسط.",
    "home.searchPlaceholder": "ابحث بالماركة، الطراز، أو برقم الإعلان (CAR-…)",
    "home.searchBtn": "ابحث بالذكاء",
    "home.cta.browse": "تصفح السوق",
    "home.cta.list": "أضف مركبتك",
    "home.featured": "مركبات مميزة",
    "home.featuredSub": "إعلانات منتقاة من معارض ووكلاء معتمدين",
    "home.modules": "منظومة كارتال",
    "home.modulesSub": "ثماني عشرة وحدة متخصصة تغطي كل مراحل حياة المركبة.",
    "home.howItWorks": "كيف يعمل كارتال",
    "home.step1.t": "اكتشف",
    "home.step1.d": "تصفح أكثر من 100,000 مركبة من الوكلاء والتجار والأفراد الموثوقين.",
    "home.step2.t": "تحقق",
    "home.step2.d": "اطلب فحصًا مستقلًا وشاهد تاريخ المركبة الكامل.",
    "home.step3.t": "أنجز الصفقة",
    "home.step3.d": "ادفع عبر ضمان كارتال — تُحرر الأموال فقط بعد تأكيد الاستلام.",
    "home.trust": "ثقة مبنية على تقنية متقدمة.",
    "home.stats.vehicles": "إعلانات مركبات",
    "home.stats.dealers": "تجار ووكلاء موثقون",
    "home.stats.providers": "شركاء خدمات",
    "home.stats.auctions": "مزادات نشطة",
    "home.partners": "مصمم للتكامل مع",
    "home.ai.title": "التقييم الذكي للمركبات",
    "home.ai.sub":
      "احصل على تقدير سعر فوري مدعوم بالذكاء الاصطناعي. فقط أدخل بيانات مركبتك.",
    "common.viewAll": "عرض الكل",
    "common.from": "ابتداءً من",
    "common.perDay": " / يوم",
    "common.year": "سنة الصنع",
    "common.km": "كم",
    "common.fairValue": "القيمة العادلة",
    "common.estimating": "جاري التقييم…",
    "common.estimate": "قيِّم السيارة",
    "common.range": "النطاق",
    "common.make": "الماركة",
    "common.model": "الطراز / الفئة",
    "common.condition": "الحالة",
    "common.summary": "الملخص",
    "common.book": "احجز الآن",
    "common.applyNow": "قدم الآن",
    "common.compare": "قارن",
    "common.bidNow": "زايد الآن",
    "common.email": "البريد الإلكتروني",
    "common.password": "كلمة المرور",
    "common.name": "الاسم الكامل",
    "common.phone": "رقم الجوال",
    "common.role": "أنا…",
    "common.company": "اسم الشركة",
    "common.signIn": "دخول",
    "common.createAccount": "إنشاء حساب",
    "common.signingIn": "جاري الدخول…",
    "common.creating": "جاري الإنشاء…",
    "common.loading": "جاري التحميل…",
    "common.empty": "لا شيء هنا بعد.",
    "common.legal":
      "تعمل كارتال حصرياً كمنصة تقنية محايدة تربط بين المشترين والبائعين ومقدمي الخدمات. كارتال ليست طرفاً في أي معاملة.",
    "footer.about": "من نحن",
    "footer.terms": "شروط الاستخدام",
    "footer.privacy": "سياسة الخصوصية",
    "footer.disclaimer": "إخلاء المسؤولية",
    "footer.refund": "سياسة الاسترداد",
    "footer.fraud": "منع الاحتيال",
    "footer.contact": "تواصل",
    "footer.copyright": "© 2026 كارتال. جميع الحقوق محفوظة.",
    "auth.signInTitle": "مرحباً بعودتك",
    "auth.signInSub": "سجل دخولك للوصول إلى لوحة تحكم كارتال.",
    "auth.registerTitle": "انضم إلى كارتال",
    "auth.registerSub": "أنشئ حساباً مجانياً في ثوانٍ.",
    "auth.switchToRegister": "جديد على كارتال؟",
    "auth.switchToLogin": "لديك حساب بالفعل؟",
    "auth.roles.individual_buyer": "مشتري فردي",
    "auth.roles.individual_seller": "بائع فردي",
    "auth.roles.business_member": "عضو منشأة",
    "auth.roles.dealer": "تاجر مركبات",
    "auth.roles.agency": "وكالة مركبات",
    "auth.roles.showroom": "معارض السيارات",
    "auth.roles.rental": "شركة تأجير",
    "auth.roles.financing": "شركة تمويل",
    "auth.roles.insurance": "شركة تأمين",
    "auth.roles.inspection": "شركة فحص",
    "auth.roles.logistics": "شركة نقل",
    "auth.roles.workshop": "ورشة / مركز خدمة",
    "auth.roles.parts": "متجر قطع غيار",
    "auth.roles.fleet": "مالك أسطول",
    "auth.roles.corporate": "عميل شركات",
    "auth.roles.exporter": "مصدّر",
    "auth.roles.marine_operator": "نشاط بحري (يخوت وزوارق)",
    "auth.roles.super_admin": "مدير عام",
    "auth.roles.admin": "مدير",
    "auth.roles.user": "مستخدم",
  },
};

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(() => {
    if (typeof window === "undefined") return "ar";
    // Support fix — first-visit language preference from the share link.
    // If the URL carries ?lang=en (or ?lang=ar), that wins over the
    // localStorage default so a recipient opening an English share link
    // lands on the English site regardless of prior visits. The value
    // is written to localStorage so subsequent navigations stick to it.
    try {
      const urlLang = new URLSearchParams(window.location.search).get("lang");
      if (urlLang === "en" || urlLang === "ar") {
        localStorage.setItem(STORAGE_KEY, urlLang);
        return urlLang;
      }
    } catch {
      /* URLSearchParams unavailable — fall through to stored default */
    }
    return localStorage.getItem(STORAGE_KEY) || "ar";
  });

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
    localStorage.setItem(STORAGE_KEY, lang);
  }, [lang]);

  const toggle = useCallback(() => setLang((l) => (l === "ar" ? "en" : "ar")), []);

  const t = useCallback(
    (key) => {
      const table = dict[lang] || dict.en;
      return table[key] ?? dict.en[key] ?? key;
    },
    [lang]
  );

  const value = useMemo(() => ({ lang, setLang, toggle, t, dir: lang === "ar" ? "rtl" : "ltr" }), [lang, t, toggle]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
}
